<template>
  <div>
    <CCard>
      <CCardHeader>
        Payout Metrics for

        <router-link
          :to="{ name: 'SellerAccountDetails', params: { id: accountId } }"
          target="_blank"
          class="ml-1"
        >
          <strong>{{ getSafe(() => sellerAccountDetails.name) }}</strong>

          <CIcon
            name="cil-external-link"
            size="custom"
            class="ml-1"
          />
        </router-link>
      </CCardHeader>

      <CCardBody>
        <div
          v-if="isLoading"
          :style="{ height: '40vh' }"
          class="d-flex flex-column justify-content-center align-items-center"
        >
          <CSpinner
            style="width: 4rem; height: 4rem"
            color="info"
            grow
          />
        </div>

        <div v-else-if="metrics">
          <!-- Averages -->
          <h6 class="text-muted text-uppercase font-weight-bold mb-3">
            Averages
          </h6>
          <CRow class="mb-4">
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetIcon
                :header="formatAmount(metrics.periodPayoutAverage)"
                text="YTD Payout Avg"
                color="primary"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-chart-pie"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetIcon
                :header="formatAmount(metrics.mtdPayoutAverage)"
                text="MTD Payout Avg"
                color="info"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-calendar"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetIcon
                :header="formatAmount(metrics.avgWeeklyPayoutPeriod)"
                text="Avg Weekly (YTD)"
                color="success"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-bar-chart"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetIcon
                :header="formatAmount(metrics.avgWeeklyPayoutMonth)"
                text="Avg Weekly (Month)"
                color="warning"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-speedometer"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
          </CRow>

          <!-- Totals: CWidgetProgress with progress bars -->
          <h6 class="text-muted text-uppercase font-weight-bold mb-3">
            Totals
          </h6>
          <CRow class="mb-4">
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetProgress
                :header="formatAmount(metrics.totalPayoutsPaid)"
                text="Total Payouts Paid"
                :footer="`${metrics.payoutCycleCount || 0} payout cycles`"
                color="primary"
                :value="100"
              />
            </CCol>
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetProgress
                :header="formatAmount(metrics.totalPayouts)"
                text="Total Payouts"
                color="info"
                :value="100"
              />
            </CCol>
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetProgress
                :header="formatAmount(metrics.totalAdjustments)"
                text="Total Adjustments"
                color="warning"
                :value="100"
              />
            </CCol>
            <CCol
              col="6"
              xl="3"
              class="mb-3"
            >
              <CWidgetProgress
                :header="formatAmount(metrics.maxSinglePayout)"
                text="Max Single Payout"
                color="success"
                :value="100"
              />
            </CCol>
          </CRow>

          <!-- Balances: CWidgetIcon for key financial indicators -->
          <h6 class="text-muted text-uppercase font-weight-bold mb-3">
            Balances &amp; Fees
          </h6>
          <CRow>
            <CCol
              col="6"
              xl="4"
              class="mb-3"
            >
              <CWidgetIcon
                :header="formatAmount(metrics.totalOutstandingBalance)"
                text="Outstanding Balance"
                color="warning"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-balance-scale"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
            <CCol
              col="6"
              xl="4"
              class="mb-3"
            >
              <CWidgetIcon
                :header="formatAmount(metrics.totalDisputeFees)"
                text="Dispute Fees"
                color="danger"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-warning"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
            <CCol
              col="6"
              xl="4"
              class="mb-3"
            >
              <CWidgetIcon
                :header="String(metrics.payoutCycleCount || 0)"
                text="Payout Cycles"
                color="primary"
                :icon-padding="false"
              >
                <CIcon
                  name="cil-loop-circular"
                  height="36"
                />
              </CWidgetIcon>
            </CCol>
          </CRow>
        </div>

        <div
          v-else
          class="text-center text-muted py-5"
        >
          Unable to retrieve metrics.
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { formatAmount } from '@/utils'
import { showErrorMessage } from '@/notification-utils'

export default {
  name: 'SellerPayoutMetrics',

  data() {
    return {
      accountId: '',
      isLoading: false,
      metrics: null
    }
  },

  computed: {
    ...mapState('accounts', [
      'sellerAccountDetails',
    ]),
  },

  async created() {
    this.accountId = this.$route.params.id

    if (this.accountId) {
      try {
        await this.getSellerAccountDetails(this.accountId)
      } catch (error) {
        console.error('Error fetching seller account details:', error)
      }
    }

    document.title = `Payout Metrics for ${this.getSafe(() => this.sellerAccountDetails.name) || this.accountId}`

    await this.fetchMetrics()
  },

  methods: {
    ...mapActions('accounts', [
      'getSellerAccountDetails',
    ]),
    ...mapActions('billing', ['getSellerPayoutMetrics']),

    async fetchMetrics() {
      this.isLoading = true
      try {
        this.metrics = await this.getSellerPayoutMetrics(this.accountId)
      } catch (error) {
        console.error('Error fetching seller metrics:', error)
        showErrorMessage('Failed to load payout metrics')
      } finally {
        this.isLoading = false
      }
    },

    formatAmount(val) {
      return formatAmount(val)
    },

    getSafe(fn) {
      try {
        return fn() 
      } catch {
        return '' 
      }
    }
  }
}
</script>

